import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../home/selectors'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors'
import { ITimelineState } from '../store/timeline.types'
import { getPatientTimelineSelector } from '../selectors/patientTimeline.selector'
import { TimelineConstants } from '../store/timeline.constants'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { setActivityLog, setEditedKeys, updateFilteredItems } from '../store/timeline.actions'
import { isProcDateTimePTA } from '../../../utilities/procedures.util'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { updateTimelineItemOnReorder } from '../util/updateTimelineItemOnReorder.util'
import { updateProcedureOnReorder } from '../util/updateProcedureOnReorder.util'

export function* setEditedItem() {
  const timeline: ITimelineState = yield select(getPatientTimelineSelector)
  const { selectedItem, editedKeys } = timeline
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const serviceConfig: IServiceConfig = yield select(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfig
  const displaySeconds = !!DISPLAY_SECONDS

  if (pcrID) {
    try {
      if (selectedItem && selectedItem.labelHeader && selectedItem.subHeader) {
        const date = selectedItem.subHeader
        const time = selectedItem.labelHeader

        const pcrDocsService = new PcrDocsService(serviceId)
        const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

        const timelineItems = pcrDoc.patient.timelineItems
        if (!timelineItems) return

        const timelineItemIndex = timelineItems.findIndex((item) => {
          return item.key === selectedItem.key
        })
        if (timelineItemIndex === -1) return

        const times = pcrDoc.times
        let isPTA = !!selectedItem.labelHeaderFlag
        if (times?.atPatientDate || times?.onSceneDate) {
          isPTA =
            isProcDateTimePTA(
              times?.atPatientDate,
              times?.atPatientTime,
              times?.onSceneDate,
              times?.onSceneTime,
              date,
              time,
              displaySeconds
            ) || false
        }

        const timelineItem = timelineItems[timelineItemIndex]
        const updatedTimelineItem = updateTimelineItemOnReorder(timelineItem, selectedItem, !!isPTA)
        timelineItems[timelineItemIndex] = updatedTimelineItem

        let updatedPatient = pcrDoc.patient
        updatedPatient = updateProcedureOnReorder(pcrDoc, updatedTimelineItem, updatedPatient)
        const newEditedKeys = [selectedItem.key]

        // Update times on child procedures, if necessary
        if (selectedItem.clientEventId && selectedItem.vsAction) {
          const childProcedureItemIndex = timelineItems.findIndex((item) => item.key === selectedItem.clientEventId)
          if (childProcedureItemIndex) {
            const childProcedureItem = timelineItems[childProcedureItemIndex]
            const updatedProcedureTimelineItem = updateTimelineItemOnReorder(childProcedureItem, selectedItem, !!isPTA)
            timelineItems[childProcedureItemIndex] = updatedProcedureTimelineItem

            newEditedKeys.push(updatedProcedureTimelineItem.key)

            updatedPatient = updateProcedureOnReorder(pcrDoc, updatedProcedureTimelineItem, updatedPatient)
          }
        }

        updatedPatient = {
          ...updatedPatient,
          timelineItems,
        }
        const pcrDocUpdated = { ...pcrDoc, patient: updatedPatient }
        yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)

        const updatedEditedKeys = [...editedKeys, ...newEditedKeys]
        yield put(setEditedKeys(updatedEditedKeys))
        yield put(setActivityLog(timelineItems))
        yield put(updateFilteredItems())
      }
    } catch (e) {
      yield put(throwPcrException(`setEditedItem() error: ${e}`))
    }
  }
}

export function* setEditedItemSaga() {
  yield takeLatest([TimelineConstants.SET_EDITED_ITEM], performWithMutexHOG, setEditedItem)
}
